import { PlpSeoBlock } from "@/components/search/plp-seo/PlpSeoBlock";
import { OpenHomeSearchTab } from "@/features/open-home/components/OpenHomeSearchTab";
import { ProBuyerSearchStrip } from "@/features/pro-buyer-price/components/ProBuyerSearchStrip";
import type { ListingPageContent } from "@/features/search/api/listing-page-content";
import type { RelatedPage } from "@/features/search/api/related-pages";
import { NewThisWeek } from "@/features/search/components/NewThisWeek";
import { PageTitle } from "@/features/search/components/PageTitle";
import { ActiveFilters } from "@/features/search/components/active-filters/ActiveFilters";
import { SearchBreadcrumbs } from "@/features/search/components/breadcrumbs/SearchBreadcrumbs";
import { FacetPanel } from "@/features/search/components/facets/FacetPanel";
import { FacetPanelMobile } from "@/features/search/components/facets/FacetPanelMobile";
import { Pagination } from "@/features/search/components/pagination/Pagination";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { ResultsCount } from "@/features/search/components/results/ResultsCount";
import { ResultsGrid } from "@/features/search/components/results/ResultsGrid";
import { SaveSearchButton } from "@/features/search/components/saved-search/SaveSearchButton";
import { CollectionPageSchema } from "@/features/search/components/seo/CollectionPageSchema";
import { RelatedPages } from "@/features/search/components/seo/RelatedPages";
import { SortSelect } from "@/features/search/components/sort/SortSelect";

/**
 * Default assembly of the search UI. Everything above the fold reads the shared context, so
 * that part is pure layout — reorder or swap parts freely without threading props.
 * Filters are a sidebar from `md` up and a slide-in panel below it.
 *
 * The SEO blocks at the foot are the exception: their content comes from Strapi and the
 * gateway rather than the search service, so `getServerSideProps` fetches it and it arrives
 * here as props. See `api/listing-page-content.ts` and `api/related-pages.ts`. The "New this
 * week" module is the third such block: its cards come from a second, anonymous search call in
 * the loader (`api/new-this-week.ts`), so they are in the HTML for crawlers. The editorial
 * guides and links inside the SEO block (WHO-5476, WHO-5534) are a committed dataset keyed by
 * locale and landing-page path, the same key the Strapi copy is fetched with, so they only
 * need the path.
 */
type SearchLayoutProps = {
  content: ListingPageContent | null;
  relatedPages: RelatedPage[];
  /** The newest listings on this page; empty when the module does not apply. */
  newThisWeek: ProductCardProduct[];
  /** Landing-page path without locale prefix; `null` for a query search. */
  pagePath?: string | null;
  /** WHO-5760: live-data intro for pages without Strapi copy; see `buildListingIntro`. */
  generatedIntro?: string | null;
};

export const SearchLayout = ({
  content,
  relatedPages,
  newThisWeek,
  pagePath,
  generatedIntro = null,
}: SearchLayoutProps) => (
  <div className="mx-auto w-full max-w-screen-xl px-4 py-6">
    {/* Renders no markup of its own — it needs the results, which only exist inside the
        provider, so it cannot sit with the head tags in `SearchSeo`. */}
    <CollectionPageSchema description={content?.seoDescription} />

    {/* Full width, above the sidebar — the trail describes the page, not the results column. */}
    <div className="flex flex-col space-y-3 pb-4">
      <SearchBreadcrumbs />
      <PageTitle />
    </div>

    {/* Spacing from `md` only: the sidebar is `display:none` below it, and `space-x` puts its
        margin on the *next* element regardless — which would indent the results on mobile. */}
    <div className="flex md:space-x-8">
      <aside className="hidden shrink-0 md:block md:w-64">
        <FacetPanel />
      </aside>

      <main className="min-w-0 flex-1">
        <div className="flex flex-col space-y-3 pb-4">
          {/* Open Home: "For sale" | "Open Home" scope, members only past the count. */}
          <OpenHomeSearchTab />
          {/* WHO-5953: Pro Buyers only, above the count it changes. */}
          <ProBuyerSearchStrip />

          {/*
           * Below `md` this is a two-row grid: count and save on the first, the filter CTA on the
           * second. `col-start-2` is what puts the CTA under save rather than at the start of its
           * row, so the two line up and come out the same width. Four controls on a single 390px
           * line wrapped both labels, which is why sorting moved inside the filter panel.
           *
           * From `md` it collapses to one flex row: the sidebar owns the filters, and the count
           * pushes the rest to the far edge.
           */}
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 md:flex md:gap-x-2">
            <div className="md:mr-auto">
              <ResultsCount />
            </div>

            <SaveSearchButton />

            {/* Sidebar takes over at `md`, so this is the only way into the filters on mobile. */}
            <div className="col-start-2 md:hidden">
              <FacetPanelMobile />
            </div>

            <SortSelect className="hidden md:flex md:w-auto" />
          </div>

          <ActiveFilters />
        </div>

        <ResultsGrid />
        <Pagination />
      </main>
    </div>

    {/* Full width again, below the sidebar — the newest listings, then the copy, guides and
        links about the page (WHO-5534), then the sibling landing pages. The module sits above
        the copy on purpose: a fresh product link is worth more to a crawler than the same link
        under a few hundred words nobody scrolls past. The SEO block renders nothing on a query
        search without copy. */}
    <div className="mt-8 flex flex-col">
      <NewThisWeek products={newThisWeek} />
      <div className="mt-6 flex flex-col">
        <PlpSeoBlock
          heading={content?.heading}
          subheading={content?.subheading}
          description={content?.description}
          pagePath={pagePath}
          generatedIntro={generatedIntro}
        />
        <RelatedPages pages={relatedPages} />
      </div>
    </div>
  </div>
);
